import { useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import * as THREE from 'three'
import GLOBE from 'vanta/dist/vanta.globe.min'

interface GlobeBackgroundProps {
  children: React.ReactNode;
  showFloatingMetrics?: boolean;
}

interface VantaConfig {
  el: HTMLDivElement | null;
  mouseControls: boolean;
  touchControls: boolean;
  gyroControls: boolean;
  minHeight: number;
  minWidth: number;
  scale: number;
  scaleMobile: number;
  color: number;
  color2: number;
  backgroundColor: number;
  size: number;
  points: number;
  maxDistance: number;
  spacing: number;
  showDots: boolean;
  opacity: number;
}

declare global {
  interface Window {
    VANTA: {
      GLOBE: (config: VantaConfig) => { destroy: () => void };
    }
  }
}

const FloatingMetrics = () => {
  const metrics = [
    { text: "312% More Leads", position: { right: "20%", top: "30%" } },
    { text: "84% Less Research Time", position: { right: "25%", top: "50%" } },
    { text: "5.3x Higher Response Rate", position: { right: "15%", top: "70%" } }
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {metrics.map((metric, index) => (
        <motion.div
          key={index}
          className="absolute bg-purple-900/20 backdrop-blur-sm rounded-lg p-3 text-white text-sm border border-purple/10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ 
            opacity: [0.5, 1, 0.5],
            y: [0, -10, 0]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            delay: index * 0.8
          }}
          style={metric.position as any}
        >
          {metric.text}
        </motion.div>
      ))}
    </div>
  );
};

export const GlobeBackground = ({ children, showFloatingMetrics = true }: GlobeBackgroundProps) => {
  const vantaRef = useRef<HTMLDivElement>(null)
  const vantaEffect = useRef<any>(null)

  useEffect(() => {
    if (!vantaEffect.current && vantaRef.current) {
      vantaEffect.current = GLOBE({
        el: vantaRef.current,
        THREE,
        mouseControls: true,
        touchControls: true,
        gyroControls: false,
        minHeight: 200.00,
        minWidth: 200.00,
        scale: 1.00,
        scaleMobile: 1.00,
        color: 0x7c3aed,
        color2: 0xa855f7,
        backgroundColor: 0x0A0A0B,
        size: 0.90,
        points: 12.00,
        maxDistance: 25.00,
        spacing: 15.00,
        showDots: true,
        opacity: 0.7
      })
    }

    return () => {
      if (vantaEffect.current) {
        vantaEffect.current.destroy()
        vantaEffect.current = null
      }
    }
  }, [])

  return (
    <div className="hero-container relative min-h-[90vh] w-full overflow-hidden">
      {/* Globe Container */}
      <div ref={vantaRef} className="absolute inset-0">
        {/* Globe Connection Effect */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(103,58,183,0.15)_0%,transparent_100%)]" />
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-dark-900/30 to-dark-900/80" />

      {/* Floating Metrics - Only show if showFloatingMetrics is true */}
      {showFloatingMetrics && <FloatingMetrics />}

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 inset-x-0 flex justify-center">
        <motion.div 
          className="flex flex-col items-center gap-2 text-white/50"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <span className="text-sm">Scroll to explore</span>
          <svg 
            className="w-6 h-6" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M19 14l-7 7m0 0l-7-7m7 7V3" 
            />
          </svg>
        </motion.div>
      </div>

      {children}
    </div>
  )
} 